import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BidderHousesProvider } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { CatalogNavProvider } from './CatalogNav';
import { PreviewLayout } from './PreviewLayout';
import { previewBasePath } from './previewNav';
import {
  resolvePreviewStatus,
  tenantRowToHouse,
  type PreviewStatus,
  type PreviewTenantRow,
} from './previewFrame.logic';

/**
 * Onboarding preview surface (spec §4, Phase E1): `/preview/<slug>`.
 *
 * Renders the draft tenant's storefront (the reused public catalog, branded)
 * so the owner can review it before going live. The public catalog read
 * policies are gated to published tenants, so a draft tenant's catalog is
 * invisible to the public path — preview works by rendering through the OWNER's
 * own session, where the `*_tenant_staff_select` RLS policies expose their own
 * draft rows.
 *
 * Authorization is enforced by RLS, not the client: resolving the tenant row by
 * slug only succeeds under `tenants_select_own_or_super`, so a non-staff visitor
 * (or anon) gets no row and sees the "no access" state; and even if they forged
 * past that, the catalog's staff-select policies would return nothing.
 */
export function PreviewFrame() {
  const { tenantSlug } = useParams();
  const { session, loading: sessionLoading, userId } = useSession();
  const [query, setQuery] = useState<{
    done: boolean;
    row: PreviewTenantRow | null;
    error: boolean;
  }>({ done: false, row: null, error: false });

  useEffect(() => {
    // Only hit the DB once auth is settled and we have a slug to resolve.
    if (sessionLoading || userId === null || !tenantSlug) return;

    let cancelled = false;
    setQuery({ done: false, row: null, error: false });
    void (async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, display_name, metadata')
        .eq('slug', tenantSlug)
        .maybeSingle<PreviewTenantRow>();
      if (cancelled) return;
      setQuery({ done: true, row: data ?? null, error: Boolean(error) });
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, sessionLoading, tenantSlug]);

  const status: PreviewStatus = resolvePreviewStatus({
    sessionLoading,
    hasSession: Boolean(session),
    tenantSlug,
    queried: query.done,
    row: query.row,
    queryError: query.error,
  });

  // Stable identity: the provider's effect keys on overrideHouse, so recomputing
  // it every render would loop. query.row is set once, so this memo is stable.
  const house = useMemo(
    () => (status === 'ready' && query.row ? tenantRowToHouse(query.row) : null),
    [status, query.row],
  );

  if (status === 'ready' && house && tenantSlug) {
    return (
      <BidderHousesProvider overrideHouse={house}>
        <CatalogNavProvider value={{ basePath: previewBasePath(tenantSlug), previewMode: true }}>
          <PreviewLayout />
        </CatalogNavProvider>
      </BidderHousesProvider>
    );
  }

  return <PreviewGate status={status} />;
}

/** Non-ready states: a plain AuctiSync-chrome centered card (no tenant theme). */
function PreviewGate({ status }: { status: PreviewStatus }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: '#f8fafc',
        color: '#0f172a',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '26rem',
          textAlign: 'center',
          border: '1px solid #e2e8f0',
          borderRadius: '0.75rem',
          background: '#ffffff',
          padding: '2rem 1.75rem',
        }}
      >
        {status === 'loading' ? (
          <p style={{ color: '#475569' }}>Loading preview…</p>
        ) : status === 'unauthenticated' ? (
          <>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              Sign in to preview
            </h1>
            <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              You need to be signed in as the auction house owner to preview this site.
            </p>
            <Link
              to="/admin"
              style={{
                display: 'inline-block',
                padding: '0.55rem 1.1rem',
                borderRadius: '0.5rem',
                background: '#0A0045',
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              Go to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              Preview unavailable
            </h1>
            <p style={{ color: '#475569', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              This preview isn&rsquo;t available on your account — either the site doesn&rsquo;t
              exist or you don&rsquo;t have access to it.
            </p>
            <Link
              to="/admin/onboarding"
              style={{
                display: 'inline-block',
                padding: '0.55rem 1.1rem',
                borderRadius: '0.5rem',
                background: '#0A0045',
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              Back to setup
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
